import { Box, styled, Switch, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import { actionCreatorAuth } from "../../auth/auth.reducer";
import { Role } from "../../auth/types";
import { IApplicationState } from "../../redux/root-reducer";
import ChangePassword from "./ChangePassword";
import styles from "./Profile.module.scss";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import VisibleFor from "auth/components/VisibleFor";
import { getCurrentMonthUnixTimeObject } from "nodeDetails/functions/dateFunctions";
import { dataInterval } from "common/types";
import {
  getInstallations,
  setSelectedInstallation,
} from "nodeDetails/nodeDetails.actions";
import { getDeviceTypeOnDisplayName } from "nodeDetails/functions/dataFunctions";
import { EmailAddress } from "./EmailAddress";
import { setIncludeVAT } from "common/common.actions";

const getStateProps = createSelector(
  (state: IApplicationState) => state.auth,
  (authState) => ({
    currentUserRole: authState.currentUser?.role,
  })
);

const SettingsBox = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

interface IProfileProps {
  changePasswordOpenInitial?: boolean;
}

const Profile = ({ changePasswordOpenInitial }: IProfileProps) => {
  const dispatch = useDispatch();
  const { includeVAT } = useSelector(
    (state: IApplicationState) => state.common
  );
  const { t } = useTranslation();
  const history = useHistory();
  const { domain }: any = useParams();

  const { currentUserRole } = useSelector(getStateProps);
  const [changePasswordOpen, setChangePasswordOpen] = useState<boolean>(
    !!changePasswordOpenInitial
  );

  const handleLogout = () => {
    if (currentUserRole === Role.Landlord) {
      history.push("/login");
    } else if (currentUserRole === Role.Tenant) {
      history.push(`${domain ? `/${domain}` : ""}/login`);
    }
    dispatch(actionCreatorAuth.logout);
  };

  const selectedNode = useSelector(
    (state: IApplicationState) => state.node.selectedNode
  );

  const selectedInstallation = useSelector(
    (state: IApplicationState) => state.node.selectedInstallation
  );

  const installations = useSelector(
    (state: IApplicationState) => state.node.installations
  );

  return (
    <>
      <Box className={styles.container}>
        <Typography variant="h1">{t("My account")}</Typography>
        <VisibleFor
          roles={[Role.Tenant]}
          child={
            <>
              <Link to={`/${domain}/profile/manage-notifications`}>
                <SettingsBox>
                  <Typography variant="h4">
                    {t("Manage notifications")}
                  </Typography>
                  <ChevronRightIcon />
                </SettingsBox>
              </Link>
            </>
          }
        ></VisibleFor>

        <VisibleFor roles={[Role.Tenant]} child={<EmailAddress />}></VisibleFor>

        <SettingsBox onClick={() => setChangePasswordOpen(true)}>
          <Typography variant="h4">{t("Change password")}</Typography>
          <ChevronRightIcon />
        </SettingsBox>
        <SettingsBox>
          <Typography variant="h4">
            {t("Show prices with VAT included")}
          </Typography>
          <Switch
            size="small"
            color="secondary"
            onChange={(e) => {
              dispatch(setIncludeVAT(e.target.checked));

              let _domain: string = `${domain}`;
              if (currentUserRole === Role.Landlord) {
                _domain = JSON.parse(
                  localStorage.getItem("domainCode") || `{ Code: "" }`
                ).Code;
              }

              if (selectedNode) {
                dispatch(getInstallations(_domain, selectedNode.ID));
              } else if (installations)
                dispatch(getInstallations(_domain, installations[0].NodeID));

              if (selectedInstallation?.displayName) {
                const currentMonthUnixTimeObject =
                  getCurrentMonthUnixTimeObject();

                if (selectedNode)
                  dispatch(
                    setSelectedInstallation(
                      _domain,
                      selectedNode.ID,
                      getDeviceTypeOnDisplayName(
                        selectedInstallation.displayName
                      ),
                      currentMonthUnixTimeObject,
                      dataInterval.Day
                    )
                  );
                else if (installations)
                  dispatch(
                    setSelectedInstallation(
                      _domain,
                      installations[0].NodeID,
                      getDeviceTypeOnDisplayName(
                        selectedInstallation.displayName
                      ),
                      currentMonthUnixTimeObject,
                      dataInterval.Day
                    )
                  );
              }
            }}
            checked={includeVAT}
          />
        </SettingsBox>
        <Box className={styles.divider} />
        <SettingsBox onClick={handleLogout}>
          <Typography variant="h4">{t("Logout")}</Typography>
          <ExitToAppIcon fontSize="small" />
        </SettingsBox>

        <Box mt="auto" ml="auto" mr="auto">
          <Typography variant="h4">{process.env.REACT_APP_VERSION}</Typography>
        </Box>
      </Box>
      <ChangePassword
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
};

export default Profile;
